import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight,
  GraduationCap,
  Wrench,
  Rocket,
  Shield,
  Briefcase,
  Sparkles,
  Compass,
  TrendingUp,
  Users,
  Bell,
  DollarSign,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const FUTUREPATH_URL = "https://agenticfuturepath.com/";

const pathways = [
  { icon: GraduationCap, label: "College" },
  { icon: Wrench, label: "Trades" },
  { icon: Shield, label: "Military" },
  { icon: Rocket, label: "Entrepreneurship" },
  { icon: Briefcase, label: "Workforce" },
];

const stats = [
  { value: "85M", label: "Jobs impacted by AI by 2030", source: "World Economic Forum" },
  { value: "97M", label: "New AI economy roles emerging", source: "World Economic Forum" },
  { value: "50%", label: "Of workers need reskilling", source: "McKinsey" },
];

const features = [
  {
    icon: Compass,
    title: "5 Career Pathways",
    description: "College, Trades, Military, Entrepreneurship & Workforce — mapped to your strengths.",
  },
  {
    icon: DollarSign,
    title: "Financial Intelligence",
    description: "Real cost breakdowns, scholarship matching, and funding gap analysis.",
  },
  {
    icon: Users,
    title: "Family Dashboard",
    description: "Parents monitor every child's progress, deadlines, and graduation timeline.",
  },
  {
    icon: Sparkles,
    title: "AI Parent Advisor",
    description: "Premium AI-powered recommendations personalized to your family.",
    premium: true,
  },
  {
    icon: TrendingUp,
    title: "Application Pipeline",
    description: "Track applications, deadlines, documents & requirements in one place.",
  },
  {
    icon: Bell,
    title: "Smart Notifications",
    description: "Email & SMS alerts for deadlines, changes, and AI-driven recommendations.",
  },
];

export default function FuturePathSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      id="futurepath"
      className="py-20 md:py-24 lg:py-28 px-6 md:px-12 lg:px-24 relative overflow-hidden"
      data-testid="futurepath-section"
    >
      {/* Background */}
      <div className="absolute inset-0 hero-gradient opacity-40" />
      <div className="absolute right-0 top-0 w-[500px] h-[500px] gradient-blob opacity-60" />
      <div className="absolute left-1/4 bottom-0 w-[400px] h-[400px] gradient-blob opacity-50" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
            animate={isInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative w-24 h-24 sm:w-28 sm:h-28 mx-auto mb-6"
          >
            <div className="absolute inset-0 bg-blue-500/30 blur-2xl rounded-full" />
            <img
              src="/futurepath-logo.png"
              alt="FuturePath AI logo"
              className="relative w-full h-full object-contain drop-shadow-[0_0_30px_rgba(59,130,246,0.6)]"
              data-testid="futurepath-logo"
            />
          </motion.div>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/40 bg-blue-500/10 mb-6">
            <Sparkles size={16} className="text-blue-400" />
            <span className="text-xs font-['Orbitron'] uppercase tracking-[0.15em] text-blue-300">
              Shea's Flagship Platform
            </span>
          </div>
          <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mb-4">
            FuturePath <span className="text-blue-400">AI</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-foreground/90 font-light max-w-3xl mx-auto mb-3">
            AI-Powered Career Navigation for the Future
          </p>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            Whether it's college, trade school, military, or entrepreneurship — FuturePath AI
            maps your unique skills to career paths that actually matter.
          </p>
        </motion.div>

        {/* Product Screenshot — shows actual dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="relative mb-14 max-w-5xl mx-auto"
          data-testid="futurepath-dashboard-preview"
        >
          {/* Glow halo behind dashboard */}
          <div className="absolute inset-0 -m-4 bg-gradient-to-tr from-blue-500/30 via-cyan-400/20 to-blue-600/30 blur-3xl rounded-3xl opacity-70" />
          {/* Dashboard frame */}
          <div className="relative rounded-2xl overflow-hidden border border-blue-500/40 shadow-[0_25px_80px_-20px_rgba(59,130,246,0.45)] bg-background/40 backdrop-blur-sm">
            {/* Browser-style top bar */}
            <div className="flex items-center gap-1.5 px-4 py-2.5 bg-background/80 border-b border-blue-500/20">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
              <span className="ml-3 text-[10px] font-mono text-muted-foreground">
                agenticfuturepath.com / future-roadmap
              </span>
            </div>
            <img
              src="/futurepath-dashboard.png"
              alt="FuturePath AI Future Roadmap dashboard — personalized AI-powered career navigation with recommended pathways, salary growth charts, and funding insights"
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
        </motion.div>

        {/* Stats — the "why this matters" punch */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12"
        >
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="neon-card p-5 text-center"
              data-testid={`futurepath-stat-${stat.value}`}
            >
              <div className="font-['Orbitron'] text-3xl lg:text-4xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-xs text-foreground/80 mb-1 leading-tight">{stat.label}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Source: {stat.source}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Pathway pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-12"
        >
          <span className="text-xs font-['Orbitron'] uppercase tracking-wider text-muted-foreground mr-1">
            5 Pathways:
          </span>
          {pathways.map((p) => (
            <div
              key={p.label}
              className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/15 hover:border-blue-400/60 transition-colors"
            >
              <p.icon className="w-4 h-4 text-blue-400" />
              <span className="text-xs sm:text-sm font-semibold text-foreground">{p.label}</span>
            </div>
          ))}
        </motion.div>

        {/* Features grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12"
        >
          {features.map((f, index) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 + index * 0.05 }}
              className="bento-card p-5 group hover:border-blue-400/50 transition-colors relative"
            >
              {f.premium && (
                <span className="absolute top-3 right-3 text-[9px] font-['Orbitron'] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/40">
                  Premium
                </span>
              )}
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-3 group-hover:bg-blue-500/20 transition-colors">
                <f.icon className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="font-['Orbitron'] text-sm font-bold uppercase text-foreground mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA + trust signals */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center"
        >
          <a
            href={FUTUREPATH_URL}
            target="_blank"
            rel="noopener"
            className="inline-block"
            data-testid="futurepath-cta-btn"
          >
            <Button
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-10 h-16 font-bold uppercase tracking-wider text-base sm:text-lg transition-all shadow-[0_0_30px_rgba(59,130,246,0.5)] hover:shadow-[0_0_45px_rgba(59,130,246,0.7)] group"
            >
              <Compass size={22} className="mr-3" />
              Get Started on FuturePath AI
              <ArrowRight size={20} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              Instant Access
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              Family Plans Available
            </span>
            <span className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-blue-400" />
              COPPA & FERPA Compliant
            </span>
          </div>

          <a
            href={FUTUREPATH_URL}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 mt-4 text-xs text-muted-foreground hover:text-blue-400 transition-colors"
            data-testid="futurepath-domain-link"
          >
            agenticfuturepath.com
            <ExternalLink className="w-3 h-3" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
